import { Fragment } from "react";
import { ArrowLeftRight, ChevronDown, Loader2 } from "lucide-react";
import { reais } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import { AvatarDaOrganizacao } from "@/components/layout/AvatarDaOrganizacao";
import { AcoesDaOrganizacao } from "./AcoesDaOrganizacao";
import { DetalheDaLinha } from "./DetalheDaLinha";
import { COLUNAS, type Coluna } from "./colunas";
import { papelDe, type Grupo, type OrgDoPainel } from "./painel";

type Props = {
  grupo: Grupo;
  ativaId: string | undefined;
  naoLidas: Record<string, number>;
  trocando: string | null;
  aberta: string | null;
  onTrocar: (o: OrgDoPainel) => void;
  onAlternar: (o: OrgDoPainel) => void;
  onRecolher: (o: OrgDoPainel) => void;
};

function Numero({ coluna, valor }: { coluna: Coluna; valor: number | null | undefined }) {
  if (valor == null) return <span className="text-zinc-300 dark:text-muted-foreground/50">—</span>;
  const ruim = valor > 0 && coluna.tom;
  return (
    <span
      className={cn(
        "tabular-nums",
        !valor ? "text-zinc-400 dark:text-muted-foreground" : "font-semibold text-zinc-900 dark:text-foreground",
        ruim === "perigo" && "text-red-600 dark:text-red-400",
        ruim === "aviso" && "text-amber-600 dark:text-amber-400",
      )}
    >
      {coluna.dinheiro ? reais(valor) : valor}
    </span>
  );
}

/** Um grupo do painel (Clínicas, Laboratórios...) como tabela: cada número na sua coluna, os ruins
 *  em cor. Clicar na linha expande o mês dela logo abaixo; "Trocar" e os três pontinhos não. */
export function TabelaDasOrganizacoes({ grupo, ativaId, naoLidas, trocando, aberta, onTrocar, onAlternar, onRecolher }: Props) {
  const colunas = COLUNAS[grupo.chave];
  return (
    <section className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead>
            <tr className="border-b border-zinc-100 bg-zinc-50/60 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:border-border dark:bg-muted/30 dark:text-muted-foreground">
              <th className="px-4 py-2.5 text-left">{grupo.titulo} · {grupo.orgs.length}</th>
              {colunas.map((c) => (
                <th key={c.rotulo} className="whitespace-nowrap px-3 py-2.5 text-right">{c.rotulo}</th>
              ))}
              <th className="w-px px-3 py-2.5" aria-label="Ações" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-border/50">
            {grupo.orgs.map((o) => {
              const ativa = o.id === ativaId;
              const n = naoLidas[o.id] ?? 0;
              const expandida = aberta === o.id;
              return (
                <Fragment key={o.id}>
                <tr id={`org-${o.id}`} onClick={() => onAlternar(o)} className={cn("cursor-pointer hover:bg-zinc-50/60 dark:hover:bg-muted/40", ativa && "bg-emerald-50/40 dark:bg-emerald-500/5", expandida && "bg-zinc-50/60 dark:bg-muted/30")}>
                  <td className="px-4 py-3">
                    <button type="button" aria-expanded={expandida} onClick={(e) => (e.stopPropagation(), onAlternar(o))} className="flex min-w-0 items-center gap-3 text-left">
                      <ChevronDown className={cn("h-4 w-4 shrink-0 text-zinc-400 transition-transform", expandida ? "rotate-0" : "-rotate-90")} />
                      <AvatarDaOrganizacao id={o.id} nome={o.nome} logo={o.logoUrl} pessoal={o.pessoal} className="h-9 w-9" />
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate font-semibold text-zinc-900 dark:text-foreground">{o.nome}</span>
                          {n > 0 && (
                            <span title={`${n} não lidas`} className="inline-flex h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-zinc-100 px-1 text-[10px] font-bold text-zinc-600 dark:bg-muted dark:text-muted-foreground">
                              {n > 99 ? "99+" : n}
                            </span>
                          )}
                        </span>
                        <span className="block truncate text-[11px] text-zinc-500 dark:text-muted-foreground">{papelDe(o)}</span>
                      </span>
                    </button>
                  </td>
                  {colunas.map((c) => (
                    <td key={c.rotulo} className="whitespace-nowrap px-3 py-3 text-right">
                      <Numero coluna={c} valor={c.valor(o)} />
                    </td>
                  ))}
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {ativa ? (
                        <span className="whitespace-nowrap rounded-md bg-emerald-100 px-2 py-1 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">Você está aqui</span>
                      ) : (
                        <button
                          type="button"
                          disabled={!!trocando}
                          onClick={(e) => (e.stopPropagation(), onTrocar(o))}
                          className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-zinc-200 px-2.5 text-[12px] font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 dark:border-border dark:text-foreground dark:hover:bg-muted"
                        >
                          {trocando === o.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowLeftRight className="h-3.5 w-3.5" />}
                          Trocar
                        </button>
                      )}
                      <AcoesDaOrganizacao org={o} onDetalhe={() => onAlternar(o)} />
                    </div>
                  </td>
                </tr>
                {expandida && (
                  <tr>
                    <td colSpan={colunas.length + 2} className="p-0">
                      <DetalheDaLinha org={o} ativa={ativa} trocando={trocando === o.id} onTrocar={() => onTrocar(o)} onRecolher={() => onRecolher(o)} />
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
